import React, { useCallback, useMemo, useRef, useState } from 'react'

import { useTranslation } from '@/core/i18n/useTranslation'
import Icon from '@/shared/components/atoms/Icon'
import { NavButton } from '@/shared/components/atoms/NavButton'

import { Integration } from '@core/api/entities/integration'

import IntegrationsCarousel, {
	IntegrationsCarouselRef,
} from './IntegrationsCarousel'

type IntegrationsTab = 'integrations' | 'addons'

interface IntegrationsGridProps {
	integrations: Integration[]
	activeTab: IntegrationsTab
}

interface ScrollState {
	canScrollLeft: boolean
	canScrollRight: boolean
}

interface IntegrationCategoryConfig {
	id: 'foot_traffic' | 'retail' | 'hospitality' | 'weather'
	labelKey: string
	integrationKeys: string[]
}

interface IntegrationSection {
	id: string
	title: string
	items: Integration[]
}

const INTEGRATION_CATEGORIES: IntegrationCategoryConfig[] = [
	{
		id: 'foot_traffic',
		labelKey: 'integrations.categories.foot_traffic',
		integrationKeys: ['foot_traffic'],
	},
	{
		id: 'retail',
		labelKey: 'integrations.categories.retail',
		integrationKeys: [
			'retail_next',
			'oracle',
			'footfall',
			'lastapp',
			'vcount',
			'tecbrain',
			'followup',
		],
	},
	{
		id: 'hospitality',
		labelKey: 'integrations.categories.hospitality',
		integrationKeys: [
			'fudo',
			'cover_manager',
			'hostme',
			'square',
			'yelp',
			'eatapp',
			'opentable',
			'toast',
			'lightspeed',
			'sevenrooms',
			'resdiary',
			'cloudbeds',
			'kliente_tech',
			'poken',
		],
	},
	{
		id: 'weather',
		labelKey: 'integrations.categories.weather',
		integrationKeys: ['weather'],
	},
]

const extractIntegrationKey = (integrationName: string): string | null => {
	const match = integrationName.match(/options\.([^.]+)\.name$/)
	return match?.[1] ?? null
}

const CategorySection: React.FC<IntegrationSection> = ({ title, items }) => {
	const carouselRef = useRef<IntegrationsCarouselRef>(null)
	const [scrollState, setScrollState] = useState<ScrollState>({
		canScrollLeft: false,
		canScrollRight: true,
	})
	const handleScrollStateChange = useCallback(
		(canScrollLeft: boolean, canScrollRight: boolean) => {
			setScrollState((prev) => {
				if (
					prev.canScrollLeft === canScrollLeft &&
					prev.canScrollRight === canScrollRight
				) {
					return prev
				}

				return { canScrollLeft, canScrollRight }
			})
		},
		[]
	)

	if (!items.length) return null

	return (
		<div className="mb-8">
			<div className="flex items-center justify-between mb-4">
				<h2 className="text-xl md:text-2xl font-normal text-foreground m-0">
					{title}
				</h2>
				<div className="hidden md:flex items-center gap-2">
					<NavButton
						onClick={() => carouselRef.current?.scrollLeft()}
						disabled={!scrollState.canScrollLeft}
					>
						<Icon
							name="chevron-left"
							width="16px"
							height="16px"
							color="white"
						/>
					</NavButton>
					<NavButton
						onClick={() => carouselRef.current?.scrollRight()}
						disabled={!scrollState.canScrollRight}
					>
						<Icon
							name="chevron-right"
							width="16px"
							height="16px"
							color="white"
						/>
					</NavButton>
				</div>
			</div>
			<IntegrationsCarousel
				ref={carouselRef}
				integrations={items}
				onScrollStateChange={handleScrollStateChange}
			/>
		</div>
	)
}

const IntegrationsGrid: React.FC<IntegrationsGridProps> = ({
	integrations,
	activeTab,
}) => {
	const { t } = useTranslation('common')

	const sections = useMemo<IntegrationSection[]>(() => {
		if (!integrations.length) return []

		if (activeTab === 'addons') {
			return [
				{
					id: 'addons',
					title: t('integrations.addons.title'),
					items: integrations,
				},
			]
		}

		const keyToCategory = new Map<string, IntegrationCategoryConfig['id']>()
		INTEGRATION_CATEGORIES.forEach((category) => {
			category.integrationKeys.forEach((integrationKey) => {
				keyToCategory.set(integrationKey, category.id)
			})
		})

		const grouped = new Map<IntegrationCategoryConfig['id'], Integration[]>()
		INTEGRATION_CATEGORIES.forEach((category) => grouped.set(category.id, []))
		const uncategorized: Integration[] = []

		integrations.forEach((integration) => {
			const integrationKey = extractIntegrationKey(integration.name)
			const categoryId = integrationKey
				? keyToCategory.get(integrationKey)
				: null

			if (categoryId) {
				grouped.get(categoryId)?.push(integration)
			} else {
				uncategorized.push(integration)
			}
		})

		const categorizedSections: IntegrationSection[] =
			INTEGRATION_CATEGORIES.map((category) => ({
				id: category.id,
				title: t(category.labelKey),
				items: grouped.get(category.id) ?? [],
			})).filter((section) => section.items.length > 0)

		if (uncategorized.length) {
			categorizedSections.push({
				id: 'other',
				title: t('integrations.categories.other'),
				items: uncategorized,
			})
		}

		return categorizedSections
	}, [integrations, activeTab, t])

	if (!sections.length) return null

	return (
		<div className="w-full">
			{sections.map((section) => (
				<CategorySection
					key={section.id}
					id={section.id}
					title={section.title}
					items={section.items}
				/>
			))}
		</div>
	)
}

export default IntegrationsGrid
